import { useMemo, useState } from 'react'
import { AttorneysSectionData } from '../../../dtos/applicationData'
import { useApplication } from '../../../context/application'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  FormContainer,
  FormItemContainer,
} from '../../../components/application/defaultComponents'
import {
  DefaultFormContainer,
  FormItemTitle,
  FormItemSubtitle,
  FlexDiv,
  AddressOptions,
  FormInputErrorMessage
} from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import StepBullet from '../../../components/basic/StepBullet'
import OptionReminder from '../../../components/application/formComponents/OptionReminder'
import OptionText from '../../../components/application/formComponents/OptionText'
import OptionButton from 'src/components/basic/OptionButton'
import StandardInput from 'src/components/basic/StandardInput'
import StandardRadio from 'src/components/basic/StandardRadio'
import { LeftBar } from 'src/components/basic/LeftBar'
import { ValidationError } from 'yup'
import { onlyICOCAttorneys } from 'src/utils/newApplications'
import { AttorneyRelationshipsSchema } from 'src/yup/NewAppSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

const Relationships = () => {
  const { application } = useApplication()
  const [attorneysSection, setAttorneysSection] = useState<AttorneysSectionData>(application?.attorneysSection ?? {})
  const [isOtherOptionChecked, setIsOtherOptionChecked] = useState(
    !!application?.attorneysSection?.reasonLessThanFourtyHours &&
    application?.attorneysSection?.reasonLessThanFourtyHours !== 'Family' &&
    application?.attorneysSection?.reasonLessThanFourtyHours !== 'Leisure'
  )
  const [showErrors, setShowErrors] = useState(false)
  const [icocValidation, setIcocValidation] = useState('')

  const validateItems = useMemo(() => {
    try {
      AttorneyRelationshipsSchema.validateSync({...attorneysSection, icocAttorneys: onlyICOCAttorneys(application).length}, { abortEarly: false })

      setIcocValidation('')
      return []
    } catch (e) {
      const err = e as ValidationError
      const steps = err.errors.map(error => error === 'NO_ICOC' || error === 'HAS_ICOC' ? 'step2' : error)
      setIcocValidation(err.errors.includes('NO_ICOC') ? 'NO_ICOC' : err.errors.includes('HAS_ICOC') ? 'HAS_ICOC' : '')

      return steps
    }
  }, [application, attorneysSection])

  const checkIfItemIsValid = (keys: string[]) => {
    return !keys.some(key => validateItems.includes(key))
  }

  const handleSolePractitionerChange = (value: boolean) => {
    setAttorneysSection(prev => ({
      ...prev,
      isSolePractitioner: value,
      solePractitionerThisYear: undefined,
      solePractitionerLastYear: undefined,
      solePractitionerTwoYearsAgo: undefined,
      solePractitionerFourYearsAgo: undefined,
      reasonLessThanFourtyHours: undefined
    }))
    setIsOtherOptionChecked(false)
  }

  const updatedApplicationData = () => ({
    ...application,
    currentPage: window.location.pathname,
    attorneysSection
  })

  const goNextRoute = () => {
    if (attorneysSection.hasICOCAttorneys)
      return '/attorneys/ic-oc'
    return '/insurance/last-insurances'
  }

  const scrollIfInvalidPage = () => {
    if(validateItems.length > 0) {
      setShowErrors(true)
      document.getElementById(validateItems[0])?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='75%'>
        <DefaultTitle>Tell us more about specific relationships or services of your firm or members of your firm.</DefaultTitle>
        <FormContainer maxWidth='760px' style={{ marginTop: '90px' }}>
          <FormItemContainer maxWidth='100%'>
            <StepBullet isComplete={!!attorneysSection?.attorneysLeftFirmQuantity}>1</StepBullet>
            <DefaultFormContainer>
              <FormItemTitle>How many attorneys have left the firm in the past 12 months?</FormItemTitle>
              <FormItemSubtitle>This does not include simple referral or co-counsel relationships.</FormItemSubtitle>
              <FlexDiv maxWidth='90px' margin='24px 0 0 0'>
                <StandardInput
                  label=""
                  type="number"
                  value={attorneysSection?.attorneysLeftFirmQuantity ?? ''}
                  onChange={e => setAttorneysSection(prev => ({ ...prev, attorneysLeftFirmQuantity: e.target.value }))}
                  errorCode='EC239'
                />
              </FlexDiv>
            </DefaultFormContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step2" isComplete={checkIfItemIsValid(['step2'])}>2</StepBullet>
            <OptionReminder
              title={`Does any lawyer(s) act as an independent contractor (IC) and/or "Of Counsel" (OC) to your firm or is listed as such on your firm's letterhead or website?`}
              reminderTitle='Additional Information Required'
              reminderText={`You will need to fill out additional independent contractor and “Of Counsel” information on the next page regarding this answer.`}
              showOptions={true}
              selectedOption={attorneysSection?.hasICOCAttorneys}
              setSelectedOption={option => setAttorneysSection(prev => ({ ...prev, hasICOCAttorneys: option }))}
              isIncorrect={validateItems.includes('step2') && showErrors}
              errorText={
                icocValidation === 'NO_ICOC' ? 
                'You did not indicate any attorneys with Independent Contractor or Of Counsel positions on the previous page. Please make sure that you answer this question accordingly or correct your attorney’s positions.' :
                icocValidation === 'HAS_ICOC' ?
                'You added attorneys with Independent Contractor or Of Counsel positions on the previous page. Please make sure that you answer this question accordingly or correct your attorney’s positions.' :
                undefined
              }
              errorCode='EC240'
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step3" isComplete={checkIfItemIsValid(['step3'])}>3</StepBullet>
            <OptionText
              title={`Does any firm member act as “Of Counsel” or Independent Contract Attorney to another law firm?`}
              textHint={`Please provide the name(s) of the attorney who is acting as such; the name and address of the other firm(s); their average hours per week in that capacity; and whether the other firm(s) are insured.`}
              showOptions={true}
              text={attorneysSection?.hasICOCInAnotherFirmDescription}
              setText={text => setAttorneysSection(prev => ({ ...prev, hasICOCInAnotherFirmDescription: text ?? undefined }))}
              selectedOption={attorneysSection?.hasICOCInAnotherFirm}
              setSelectedOption={option => setAttorneysSection(prev => ({ ...prev, hasICOCInAnotherFirm: option, hasICOCInAnotherFirmDescription: undefined }))}
              isIncorrect={validateItems.includes('step3') && showErrors}
              errorCodes={['EC241', 'EC242']} 
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet
              id="step4"
              isComplete={checkIfItemIsValid(['step4'])}
            >
              4
            </StepBullet>
            <DefaultFormContainer>
              <FormItemTitle>Is Applicant a sole practitioner?</FormItemTitle>
              <AddressOptions>
                <OptionButton
                  checked={attorneysSection?.isSolePractitioner === true}
                  onChange={() => handleSolePractitionerChange(true)}
                  isIncorrect={validateItems.includes('step4') && showErrors && (attorneysSection?.isSolePractitioner === undefined || attorneysSection?.isSolePractitioner === null)}
                  errorCode='EC243'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={attorneysSection?.isSolePractitioner === false}
                  onChange={() => handleSolePractitionerChange(false)}
                  isIncorrect={validateItems.includes('step4') && showErrors && (attorneysSection?.isSolePractitioner === undefined || attorneysSection?.isSolePractitioner === null)}
                  errorCode='EC243'
                >
                  No
                </OptionButton>
              </AddressOptions>
              <ValidationErrorMessage errorCode='EC243' />
              {validateItems.includes('step4') && showErrors && (attorneysSection?.isSolePractitioner === undefined || attorneysSection?.isSolePractitioner === null) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
              {attorneysSection?.isSolePractitioner === true ? (
                <FlexDiv margin='24px 0 0 0' gap="24px">
                  <LeftBar />
                  <FlexDiv dir="column" gap="24px">
                    <FormItemTitle style={{ fontSize: '14px' }}>How many hours does Applicant work on average per week for this firm (not including work performed as an Independent Contractor or Of Counsel or any other employment).</FormItemTitle>
                    <FlexDiv dir="column" gap="24px" maxWidth='200px'>
                      <StandardInput
                        label="This year"
                        type="number"
                        value={attorneysSection?.solePractitionerThisYear ?? ''}
                        onChange={e => setAttorneysSection(prev => ({ ...prev, solePractitionerThisYear: e.target.value }))}
                        errorCode='EC244'
                      />
                      <StandardInput
                        label="Last year"
                        type="number"
                        value={attorneysSection?.solePractitionerLastYear ?? ''}
                        onChange={e => setAttorneysSection(prev => ({ ...prev, solePractitionerLastYear: e.target.value }))}
                        isIncorrect={validateItems.includes('step4') && showErrors && !attorneysSection?.solePractitionerLastYear}
                        errorMessage={validateItems.includes('step4') && showErrors && !attorneysSection?.solePractitionerLastYear ? 'This is a required field' : ''}
                      />
                      <StandardInput
                        label="Two years ago"
                        type="number"
                        value={attorneysSection?.solePractitionerTwoYearsAgo ?? ''}
                        onChange={e => setAttorneysSection(prev => ({ ...prev, solePractitionerTwoYearsAgo: e.target.value }))}
                      />
                      <StandardInput
                        label="Three years ago"
                        type="number"
                        value={attorneysSection?.solePractitionerFourYearsAgo ?? ''}
                        onChange={e => setAttorneysSection(prev => ({ ...prev, solePractitionerFourYearsAgo: e.target.value }))}
                      />
                    </FlexDiv>
                    <FlexDiv dir="column" gap="20px" maxWidth='300px'>
                      <FormItemTitle style={{ fontSize: '14px' }}>If less than 40 hours, please provide the reason:</FormItemTitle>
                      <StandardRadio
                        label="Family"
                        value="Family"
                        checked={attorneysSection?.reasonLessThanFourtyHours === 'Family'}
                        onChange={() => {}}
                        onClick={(e) => {
                          const {value} = e.currentTarget
                          setAttorneysSection(prev => ({
                            ...prev,
                            reasonLessThanFourtyHours: prev.reasonLessThanFourtyHours === value ? '' : value
                          }))
                          setIsOtherOptionChecked(false)
                        }}
                      />
                      <StandardRadio
                        label="Leisure"
                        value="Leisure"
                        checked={attorneysSection?.reasonLessThanFourtyHours === 'Leisure'}
                        onChange={() => {}}
                        onClick={(e) => {
                          const {value} = e.currentTarget
                          setAttorneysSection(prev => ({
                            ...prev,
                            reasonLessThanFourtyHours: prev.reasonLessThanFourtyHours === value ? '' : value
                          }))
                          setIsOtherOptionChecked(false)
                        }}
                      />
                      <FlexDiv gap="8px" align='center'>
                        <StandardRadio
                          label="Other: "
                          value="Other"
                          checked={isOtherOptionChecked}
                          onChange={() => {}}
                          onClick={() => {
                            setIsOtherOptionChecked(prev => !prev)
                            setAttorneysSection(prev => ({ ...prev, reasonLessThanFourtyHours: '' }))
                          }}
                        />
                        <StandardInput
                          label=""
                          disabled={!isOtherOptionChecked}
                          value={isOtherOptionChecked ? attorneysSection?.reasonLessThanFourtyHours : ''}
                          onChange={e => setAttorneysSection(prev => ({ ...prev, reasonLessThanFourtyHours: e.target.value }))}
                        />
                      </FlexDiv>
                    </FlexDiv>
                  </FlexDiv>
                </FlexDiv>
              ) : null}
            </DefaultFormContainer>
          </FormItemContainer>
        </FormContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/attorneys/main"
        isFormValid={validateItems.length === 0}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={scrollIfInvalidPage}
      />
    </ApplicationBodyContainer>
  )
}

export default Relationships
